import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wallet, Users, AlertTriangle, CheckCircle, Clock, Ban,
  ChevronRight, Calendar, Euro, FileText, Download, Search,
  Loader2, RefreshCw, ArrowRight, Info, Filter, X,
  CircleDollarSign, TrendingUp, AlertCircle, History, Crown, RotateCcw,
  Link2, ShieldCheck, ShieldOff, ShieldAlert
} from 'lucide-react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const AdminPaymentsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorOrders, setVendorOrders] = useState(null);
  const [vendorOrdersLoading, setVendorOrdersLoading] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [adminRefunds, setAdminRefunds] = useState(null);
  const [subscriptionsSummary, setSubscriptionsSummary] = useState(null);
  
  // Ledger balance nel dialog vendor
  const [vendorLedgerBalance, setVendorLedgerBalance] = useState(null);

  // Stripe Connect admin-link
  const [stripeAdminLinkInput, setStripeAdminLinkInput] = useState('');
  const [stripeAdminLinking, setStripeAdminLinking] = useState(false);

  // Process payout dialog
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [processingPayout, setProcessingPayout] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    payment_date: new Date().toLocaleDateString('it-IT'),
    payment_method: 'bonifico',
    payment_reference: '',
    notes: ''
  });

  // Check permissions
  useEffect(() => {
    if (user && !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      toast.error('Accesso non autorizzato');
    }
  }, [user, navigate]);

  // Load subscriptions summary
  useEffect(() => {
    const fetchSubscriptionsSummary = async () => {
      try {
        const res = await api.get('/subscriptions/admin/summary');
        setSubscriptionsSummary(res.data.data);
      } catch (error) {
        console.error('Error fetching subscriptions summary:', error);
      }
    };
    fetchSubscriptionsSummary();
  }, []);

  // Load dashboard data when period changes
  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const [dashRes, historyRes, refundsRes] = await Promise.allSettled([
          api.get('/admin/payments/dashboard', { params: { period: selectedPeriod } }),
          api.get('/admin/payments/payout-history', { params: { limit: 20 } }),
          api.get('/vendor/admin/refunds', { params: { limit: 30 } })
        ]);

        if (dashRes.status === 'fulfilled') {
          setDashboard(dashRes.value.data.data);
        } else {
          console.error('Dashboard fetch error:', dashRes.reason);
          toast.error('Errore nel caricamento dati payout');
        }
        if (historyRes.status === 'fulfilled') {
          setPayoutHistory(historyRes.value.data.data.payouts);
        } else {
          console.error('History fetch error:', historyRes.reason);
        }
        if (refundsRes.status === 'fulfilled') {
          setAdminRefunds(refundsRes.value.data.data);
        } else {
          console.error('Refunds fetch error:', refundsRes.reason);
        }
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, [selectedPeriod]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [dashRes, historyRes, refundsRes] = await Promise.allSettled([
        api.get('/admin/payments/dashboard', { params: { period: selectedPeriod } }),
        api.get('/admin/payments/payout-history', { params: { limit: 20 } }),
        api.get('/vendor/admin/refunds', { params: { limit: 30 } })
      ]);

      if (dashRes.status === 'fulfilled') {
        setDashboard(dashRes.value.data.data);
      } else {
        console.error('Dashboard fetch error:', dashRes.reason);
        toast.error('Errore nel caricamento dati payout');
      }
      if (historyRes.status === 'fulfilled') {
        setPayoutHistory(historyRes.value.data.data.payouts);
      } else {
        console.error('History fetch error:', historyRes.reason);
      }
      if (refundsRes.status === 'fulfilled') {
        setAdminRefunds(refundsRes.value.data.data);
      } else {
        console.error('Refunds fetch error:', refundsRes.reason);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorOrders = async (vendor) => {
    setSelectedVendor(vendor);
    setVendorOrdersLoading(true);
    setVendorLedgerBalance(null);
    try {
      const [ordersRes, balanceRes] = await Promise.allSettled([
        api.get(`/admin/payments/vendor/${vendor.producer_id}/orders`, {
          params: { period: selectedPeriod }
        }),
        api.get(`/admin/vendors/${vendor.producer_id}/balance`),
      ]);
      if (ordersRes.status === 'fulfilled') setVendorOrders(ordersRes.value.data.data);
      else toast.error('Errore nel caricamento ordini');
      if (balanceRes.status === 'fulfilled') setVendorLedgerBalance(balanceRes.value.data);
    } catch (error) {
      console.error('Error fetching vendor orders:', error);
      toast.error('Errore nel caricamento ordini');
    } finally {
      setVendorOrdersLoading(false);
    }
  };

  const handleAdminStripeLink = async (producerId) => {
    if (!stripeAdminLinkInput.startsWith('acct_')) {
      toast.error("Inserisci un ID Stripe valido (inizia con 'acct_')");
      return;
    }
    setStripeAdminLinking(true);
    try {
      await api.post(`/vendors/${producerId}/stripe/connect/admin-link`, {
        stripe_account_id: stripeAdminLinkInput.trim(),
      });
      toast.success('Account Stripe collegato. Ricarica la lista vendor per vedere il nuovo stato.');
      setStripeAdminLinkInput('');
      // Aggiorna il vendor nella lista locale
      setVendors(prev => prev.map(v =>
        v.producer_id === producerId
          ? { ...v, stripe_account_id: stripeAdminLinkInput.trim(), stripe_onboarding_completed: true }
          : v
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante il link Stripe.');
    } finally {
      setStripeAdminLinking(false);
    }
  };

  const handleProcessPayout = async () => {
    if (!payoutForm.payment_reference.trim()) {
      toast.error('Inserisci il riferimento bonifico');
      return;
    }
    
    setProcessingPayout(true);
    try {
      const res = await api.post(`/admin/payments/process-payout/${selectedVendor.producer_id}`, null, {
        params: {
          payment_date: payoutForm.payment_date,
          payment_method: payoutForm.payment_method,
          payment_reference: payoutForm.payment_reference,
          notes: payoutForm.notes || undefined,
          period: selectedPeriod
        }
      });
      
      toast.success(res.data.message);
      setProcessDialogOpen(false);
      setSelectedVendor(null);
      setVendorOrders(null);
      setPayoutForm({
        payment_date: new Date().toLocaleDateString('it-IT'),
        payment_method: 'bonifico',
        payment_reference: '',
        notes: ''
      });
      fetchDashboard();
    } catch (error) {
      console.error('Error processing payout:', error);
      toast.error(error.response?.data?.detail || 'Errore nel processamento payout');
    } finally {
      setProcessingPayout(false);
    }
  };

  const getPayoutStatusBadge = (status) => {
    switch (status) {
      case 'DA_PAGARE':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">Da Pagare</Badge>;
      case 'PAGATO':
        return <Badge className="bg-green-100 text-green-800 border-green-300">Pagato</Badge>;
      case 'PARZIALE':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-300">Parziale</Badge>;
      case 'BLOCCATO':
        return <Badge className="bg-red-100 text-red-800 border-red-300">Bloccato</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  const getStripeBadge = (vendor) => {
    if (!vendor.stripe_account_id) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
          <ShieldOff className="w-3 h-3" />
          Non collegato
        </span>
      );
    }
    if (!vendor.stripe_onboarding_completed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">
          <ShieldAlert className="w-3 h-3" />
          Incompleto
        </span>
      );
    }
    if (!vendor.stripe_payouts_enabled) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
          <Clock className="w-3 h-3" />
          In verifica
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
        <ShieldCheck className="w-3 h-3" />
        Attivo
      </span>
    );
  };

  const getPeriodOptions = () => {
    const now = new Date();
    const options = [
      { value: 'current', label: 'Periodo Corrente' },
      { value: 'previous', label: 'Periodo Precedente' }
    ];
    
    // Add last 6 months
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      
      options.push({
        value: `${year}-${String(month).padStart(2, '0')}-1`,
        label: `${date.toLocaleString('it-IT', { month: 'long', year: 'numeric' })} (1-15)`
      });
      options.push({
        value: `${year}-${String(month).padStart(2, '0')}-2`,
        label: `${date.toLocaleString('it-IT', { month: 'long', year: 'numeric' })} (16-fine)`
      });
    }
    
    return options;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Wallet className="w-8 h-8 text-primary" />
            Gestione Payout Venditori
          </h1>
          <p className="text-text-muted mt-1">
            Dashboard operativa per elaborazione pagamenti quindicinali
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[250px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {getPeriodOptions().map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Period Info */}
      {dashboard && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-800">
                Periodo: {dashboard.period?.label}
              </span>
            </div>
            <div className="text-sm text-blue-600">
              Prossimo payout: <strong>{dashboard.next_payout_date}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Euro className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-text-muted">Totale Payout</p>
                  <p className="text-2xl font-bold text-green-600">
                    €{(dashboard.totals?.total_payout || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-text-muted mt-1">
                    Netto prodotti + Spedizione
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-text-muted">Rimb. Spedizione</p>
                  <p className="text-2xl font-bold text-purple-600">
                    €{(dashboard.totals?.shipping_total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-text-muted mt-1">100% ai venditori</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-text-muted">Venditori da Pagare</p>
                  <p className="text-2xl font-bold">{dashboard.totals?.vendors_to_pay}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-yellow-100 rounded-xl">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-text-muted">Ordini Periodo</p>
                  <p className="text-2xl font-bold">{(dashboard.totals?.orders_ready || 0) + (dashboard.totals?.orders_paid || 0)}</p>
                  {dashboard.totals?.orders_paid > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{dashboard.totals.orders_paid} già pagati</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-xl">
                  <Ban className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-text-muted">Ordini Bloccati</p>
                  <p className="text-2xl font-bold text-red-600">{dashboard.totals?.orders_blocked}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscriptions Card */}
          <Card className="bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-accent/20 rounded-xl">
                  <Crown className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-text-muted">Abbonati Pro</p>
                  <p className="text-2xl font-bold text-accent">{subscriptionsSummary?.total_active || 0}</p>
                  <p className="text-xs text-text-muted mt-1">
                    €{((subscriptionsSummary?.total_active || 0) * 29.90).toFixed(2)}/mese
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Commission Summary */}
      {dashboard && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CircleDollarSign className="w-5 h-5" />
              Riepilogo Economico Periodo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <div>
                <p className="text-sm text-text-muted">Lordo Totale</p>
                <p className="text-xl font-semibold">€{(dashboard.totals?.gross_total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-sm text-text-muted">Commissioni DalSud</p>
                <p className="text-xl font-semibold text-primary">€{(dashboard.totals?.commission_total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 -m-2">
                <p className="text-sm text-text-muted">Totale Payout</p>
                <p className="text-xl font-bold text-green-600">€{(dashboard.totals?.total_payout || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs text-text-muted">Netto + Spedizione</p>
              </div>
              <div>
                <p className="text-sm text-text-muted">Spedizioni</p>
                <p className="text-xl font-semibold text-blue-600">+€{(dashboard.totals?.shipping_total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs text-text-muted">100% rimborsate</p>
              </div>
              <div className="opacity-70">
                <p className="text-sm text-text-muted">IVA su Commissioni</p>
                <p className="text-xl font-semibold text-orange-600">€{(dashboard.totals?.vat_total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs text-text-muted">Solo se regime IVA</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700">
                <strong>Totale Payout</strong> = Netto Prodotti + Rimborso Spedizione. 
                La spedizione è anticipata dal venditore e rimborsata al 100%.
                <strong> Regime IVA:</strong> Sottrarre anche IVA commissioni dal netto prodotti.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vendors List */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Venditori - Payout Periodo
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dashboard?.vendors?.length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nessun venditore con ordini pronti per payout in questo periodo</p>
              <p className="text-sm mt-2">Gli ordini appaiono qui solo quando sono <strong>idonei al payout</strong> (finestra recesso scaduta)</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium">Venditore</th>
                    <th className="pb-3 font-medium text-center">Ordini</th>
                    <th className="pb-3 font-medium text-right">Prodotti</th>
                    <th className="pb-3 font-medium text-right text-blue-600">Spedizione</th>
                    <th className="pb-3 font-medium text-right">Commissioni</th>
                    <th className="pb-3 font-medium text-right bg-green-50">Tot. Payout</th>
                    <th className="pb-3 font-medium text-center">Stato</th>
                    <th className="pb-3 font-medium text-center">Stripe</th>
                    <th className="pb-3 font-medium text-center">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.vendors?.map((vendor) => (
                    <tr key={vendor.producer_id} className="border-b hover:bg-gray-50">
                      <td className="py-4">
                        <div>
                          <p className="font-medium">{vendor.producer_name}</p>
                          <p className="text-xs text-text-muted">{vendor.producer_email}</p>
                        </div>
                      </td>
                      <td className="py-4 text-center">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          {vendor.orders_paid > 0 && (
                            <span className="text-gray-500">{vendor.orders_paid} ✓</span>
                          )}
                          {vendor.orders_ready > 0 && (
                            <span className="text-green-600">{vendor.orders_ready}</span>
                          )}
                          {vendor.orders_blocked > 0 && (
                            <span className="text-red-600">({vendor.orders_blocked} ⚠)</span>
                          )}
                          {vendor.orders_paid === 0 && vendor.orders_ready === 0 && vendor.orders_blocked === 0 && (
                            <span className="text-text-muted">—</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 text-right">€{vendor.gross_amount?.toFixed(2)}</td>
                      <td className="py-4 text-right text-blue-600">+€{(vendor.shipping_amount || 0).toFixed(2)}</td>
                      <td className="py-4 text-right text-red-600">-€{vendor.commission_amount?.toFixed(2)}</td>
                      <td className="py-4 text-right font-bold text-green-600 bg-green-50">
                        €{vendor.total_payout?.toFixed(2) || '0.00'}
                      </td>
                      <td className="py-4 text-center">
                        {getPayoutStatusBadge(vendor.payout_status)}
                      </td>
                      <td className="py-4 text-center">
                        {getStripeBadge(vendor)}
                      </td>
                      <td className="py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => fetchVendorOrders(vendor)}
                            className="gap-1"
                          >
                            <FileText className="w-4 h-4" />
                            Dettagli
                          </Button>
                          {(vendor.payout_status === 'DA_PAGARE' || vendor.payout_status === 'PARZIALE') && vendor.orders_ready > 0 && (
                            <Button 
                              size="sm"
                              onClick={() => {
                                fetchVendorOrders(vendor);
                                setProcessDialogOpen(true);
                              }}
                              className="gap-1 bg-green-600 hover:bg-green-700"
                              data-testid="pay-vendor-btn"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Paga
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Payouts History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5" />
            Storico Payout Recenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          {payoutHistory.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              <CircleDollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nessun payout effettuato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-2 text-left font-medium">Venditore</th>
                    <th className="pb-2 text-left font-medium">Periodo</th>
                    <th className="pb-2 text-right font-medium">Netto</th>
                    <th className="pb-2 text-center font-medium">Ordini</th>
                    <th className="pb-2 text-left font-medium">Riferimento</th>
                    <th className="pb-2 text-left font-medium">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutHistory.slice(0, 10).map((payout) => (
                    <tr key={payout.id} className="border-b hover:bg-gray-50">
                      <td className="py-2">{payout.producer_name}</td>
                      <td className="py-2">{payout.period_start} - {payout.period_end}</td>
                      <td className="py-2 text-right font-medium text-green-600">
                        €{payout.net_amount?.toFixed(2)}
                      </td>
                      <td className="py-2 text-center">{payout.orders_count}</td>
                      <td className="py-2 font-mono text-xs">{payout.payment_reference}</td>
                      <td className="py-2">{payout.processed_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rimborsi Resi */}
      {adminRefunds && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-orange-500" />
                Rimborsi Resi — Vista Globale
              </CardTitle>
              {adminRefunds.total_refunded_eur > 0 && (
                <span className="text-sm font-semibold text-orange-600">
                  Totale rimborsato: €{adminRefunds.total_refunded_eur?.toFixed(2)}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {adminRefunds.items?.length === 0 ? (
              <div className="text-center py-8 text-text-muted">
                <RotateCcw className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nessun rimborso registrato</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-text-muted">
                      <th className="text-left pb-2 px-2">Venditore</th>
                      <th className="text-left pb-2 px-2">Ordine</th>
                      <th className="text-right pb-2 px-2">Importo</th>
                      <th className="text-center pb-2 px-2">Stato</th>
                      <th className="text-left pb-2 px-2">Stripe ID</th>
                      <th className="text-left pb-2 px-2">Note</th>
                      <th className="text-left pb-2 px-2">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminRefunds.items.map((r) => (
                      <tr key={r.id} className={`border-b hover:bg-gray-50 ${r.status === 'failed' ? 'bg-red-50' : r.status === 'skipped' ? 'bg-yellow-50/40' : ''}`}>
                        <td className="py-2 px-2 font-medium">{r.producer_name || '—'}</td>
                        <td className="py-2 px-2 font-mono text-xs">{r.order_number || r.order_id?.slice(-8)}</td>
                        <td className="py-2 px-2 text-right">
                          {r.status === 'skipped' && r.expected_refund_amount ? (
                            <div>
                              <span className="text-xs line-through text-gray-400">€{r.expected_refund_amount?.toFixed(2)}</span>
                              <span className="block text-[10px] text-yellow-700 font-medium">⚠ non processato</span>
                            </div>
                          ) : (
                            <span className="font-semibold text-orange-600">€{r.amount?.toFixed(2)}</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Badge className={
                            r.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                            r.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            r.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {r.status === 'succeeded' ? 'Completato' :
                             r.status === 'pending' ? 'In corso' :
                             r.status === 'failed' ? 'Fallito' : 'Saltato'}
                          </Badge>
                        </td>
                        <td className="py-2 px-2 font-mono text-xs text-text-muted">
                          {r.stripe_refund_id || '—'}
                        </td>
                        <td className="py-2 px-2 text-xs text-text-muted">
                          {r.failure_reason || r.notes || '—'}
                        </td>
                        <td className="py-2 px-2 text-xs">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(() => {
                  const skippedWithAmount = adminRefunds.items?.filter(r => r.status === 'skipped' && r.expected_refund_amount > 0) || [];
                  return (
                    <>
                      {adminRefunds.failed_count > 0 && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <p className="text-sm text-red-700">
                            <strong>{adminRefunds.failed_count}</strong> rimborsi falliti richiedono intervento manuale.
                          </p>
                        </div>
                      )}
                      {skippedWithAmount.length > 0 && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-600" />
                          <p className="text-sm text-yellow-700">
                            <strong>{skippedWithAmount.length}</strong> rimborsi saltati (importo non era impostato al momento del reso). Verificare se il rimborso è stato eseguito manualmente o va ri-emesso.
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vendor Orders Dialog */}
      <Dialog open={!!selectedVendor && !processDialogOpen} onOpenChange={(open) => !open && setSelectedVendor(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Dettaglio Ordini - {selectedVendor?.producer_name}
            </DialogTitle>
          </DialogHeader>
          
          {vendorOrdersLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : vendorOrders ? (
            <div className="space-y-6">
              {/* Saldo Ledger */}
              {vendorLedgerBalance && (
                <div className={`border rounded-lg p-4 ${vendorLedgerBalance.total_balance < 0 ? 'border-red-200 bg-red-50' : 'border-blue-100 bg-blue-50/40'}`}>
                  <p className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1.5">
                    <CircleDollarSign className="w-3.5 h-3.5" />
                    SALDO LEDGER
                  </p>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-text-muted">Disponibile</p>
                      <p className={`font-bold ${vendorLedgerBalance.available_balance < 0 ? 'text-red-600' : 'text-green-700'}`}>
                        €{vendorLedgerBalance.available_balance.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Hold dispute</p>
                      <p className="font-bold text-yellow-700">
                        −€{Math.abs(vendorLedgerBalance.hold_balance).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Totale</p>
                      <p className={`font-bold ${vendorLedgerBalance.total_balance < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                        €{vendorLedgerBalance.total_balance.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  {vendorLedgerBalance.total_balance < 0 && (
                    <p className="text-xs text-red-700 mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Saldo negativo — il payout sarà bloccato finché non torna positivo.
                    </p>
                  )}
                </div>
              )}

              {/* Stripe Connect status + link manuale */}
              {selectedVendor && !selectedVendor.stripe_account_id && (
                <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1.5">
                    <ShieldOff className="w-3.5 h-3.5" />
                    STRIPE CONNECT — NON COLLEGATO
                  </p>
                  <p className="text-xs text-red-600 mb-3">
                    Il vendor non ha un account Stripe collegato. Se l'account esiste in Stripe dashboard
                    ma non è salvato in DB, usa il form sotto per collegarlo manualmente.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="acct_xxxxxxxxxxxxxxxxx"
                      value={stripeAdminLinkInput}
                      onChange={e => setStripeAdminLinkInput(e.target.value)}
                      className="flex-1 text-xs border border-red-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-red-400"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleAdminStripeLink(selectedVendor.producer_id)}
                      disabled={stripeAdminLinking || !stripeAdminLinkInput}
                      className="gap-1.5 text-xs"
                    >
                      {stripeAdminLinking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
                      Collega
                    </Button>
                  </div>
                </div>
              )}

              {/* Totals */}
              {(() => {
                const isPaid = vendorOrders.totals?.orders_paid > 0 && vendorOrders.totals?.orders_ready === 0;
                const totalOrders = (vendorOrders.totals?.orders_ready || 0) + (vendorOrders.totals?.orders_paid || 0);
                return (
                  <div className={`border rounded-lg p-4 ${isPaid ? 'bg-gray-50 border-gray-200' : 'bg-green-50 border-green-200'}`}>
                    <h4 className={`font-medium mb-3 flex items-center gap-2 ${isPaid ? 'text-gray-700' : 'text-green-800'}`}>
                      <CheckCircle className="w-4 h-4" />
                      {isPaid
                        ? `Riepilogo Periodo — ${totalOrders} ordini pagati`
                        : `Ordini Pronti al Payout (${vendorOrders.totals?.orders_ready})`
                      }
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                      <div>
                        <p className="text-text-muted">Lordo Prodotti</p>
                        <p className="font-semibold">€{(vendorOrders.totals?.gross_total || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-text-muted">Commissione</p>
                        <p className="font-semibold text-red-600">-€{(vendorOrders.totals?.commission_total || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-text-muted">Netto Prodotti</p>
                        <p className="font-semibold">€{(vendorOrders.totals?.net_total || 0).toFixed(2)}</p>
                      </div>
                    </div>
                    {vendorOrders.totals?.shipping_total > 0 && (
                      <div className="border-t pt-3 mb-3">
                        <div className="flex justify-between items-center">
                          <p className="text-text-muted">Rimborso Spedizione</p>
                          <p className="font-semibold text-blue-600">+€{vendorOrders.totals?.shipping_total?.toFixed(2)}</p>
                        </div>
                      </div>
                    )}
                    {vendorOrders.totals?.refund_total > 0 && (
                      <div className="border-t pt-3 mb-3">
                        <div className="flex justify-between items-center">
                          <p className="text-orange-600">Rimborsi Resi</p>
                          <p className="font-semibold text-orange-600">-€{vendorOrders.totals?.refund_total?.toFixed(2)}</p>
                        </div>
                      </div>
                    )}
                    <div className="border-t pt-3">
                      <div className="flex justify-between items-center">
                        <p className={`font-medium ${isPaid ? 'text-gray-700' : 'text-green-800'}`}>
                          {isPaid ? 'TOTALE PAGATO' : 'TOTALE PAYOUT'}
                        </p>
                        <p className={`font-bold text-xl ${isPaid ? 'text-gray-700' : 'text-green-600'}`}>
                          €{(vendorOrders.totals?.total_payout || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Ready Orders */}
              {vendorOrders.orders?.ready?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 text-green-700">Ordini Idonei al Payout</h4>
                  <div className="space-y-2">
                    {vendorOrders.orders.ready.map((order) => (
                      <div key={order.id} className={`border rounded-lg p-3 ${order.active_dispute ? 'border-red-300 bg-red-50' : 'bg-green-50/50'}`}>
                        {order.active_dispute && (
                          <div className="text-xs text-red-700 font-medium mb-1.5 flex items-center gap-1">
                            <span>⚠️</span>
                            <span>Chargeback aperto — payout bloccato · {order.active_dispute.reason?.replace(/_/g,' ')}
                              {order.active_dispute.due_by && ` · scad. ${new Date(order.active_dispute.due_by).toLocaleDateString('it-IT')}`}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{order.order_number}</span>
                            <span className="text-xs text-text-muted ml-2">
                              Consegnato: {order.delivered_at ? new Date(order.delivered_at).toLocaleDateString('it-IT') : '-'}
                            </span>
                          </div>
                          <div className="text-right text-sm">
                            <span className="text-text-muted">Payout: </span>
                            <span className="font-semibold text-green-600">
                              €{(order.economics?.net_payout ?? order.economics?.total_payout)?.toFixed(2)}
                            </span>
                            {order.economics?.shipping_cost > 0 && (
                              <span className="text-xs text-blue-600 ml-1">(incl. sped. €{order.economics?.shipping_cost?.toFixed(2)})</span>
                            )}
                            {order.economics?.refund_deducted > 0 && (
                              <span className="text-xs text-orange-500 ml-1">(−€{order.economics?.refund_deducted?.toFixed(2)} resi)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Blocked Orders */}
              {vendorOrders.orders?.blocked?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Ordini Bloccati ({vendorOrders.orders.blocked.length})
                  </h4>
                  <div className="space-y-2">
                    {vendorOrders.orders.blocked.map((order) => (
                      <div key={order.id} className="border border-red-200 rounded-lg p-3 bg-red-50">
                        {order.active_dispute && (
                          <div className="text-xs text-red-700 font-medium mb-1.5 flex items-center gap-1">
                            <span>⚠️</span>
                            <span>Chargeback — {order.active_dispute.reason?.replace(/_/g,' ')} · €{order.active_dispute.amount?.toFixed(2)}
                              {order.active_dispute.due_by && ` · scad. ${new Date(order.active_dispute.due_by).toLocaleDateString('it-IT')}`}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{order.order_number}</span>
                            <Badge variant="destructive" className="ml-2 text-xs">Bloccato</Badge>
                          </div>
                          <span className="text-sm text-text-muted">
                            €{order.economics?.vendor_net?.toFixed(2)} (in sospeso)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-red-600 mt-2">
                    ⚠️ Gli ordini bloccati NON saranno inclusi nel payout
                  </p>
                </div>
              )}

              {/* Paid Orders */}
              {vendorOrders.orders?.paid?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 text-gray-600 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Ordini Già Pagati ({vendorOrders.orders.paid.length})
                  </h4>
                  <div className="space-y-2">
                    {vendorOrders.orders.paid.map((order) => (
                      <div key={order.id} className={`border rounded-lg p-3 ${order.active_dispute ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-gray-50'}`}>
                        {order.active_dispute && (
                          <div className="text-xs text-red-700 font-medium mb-1.5 flex items-center gap-1">
                            <span>⚠️</span>
                            <span>Chargeback su ordine già pagato — verifica audit · {order.active_dispute.reason?.replace(/_/g,' ')} · €{order.active_dispute.amount?.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{order.order_number}</span>
                            <Badge variant="outline" className="ml-2 text-xs text-gray-500">Pagato</Badge>
                          </div>
                          <div className="text-right text-sm">
                            <span className="text-gray-500">Payout: </span>
                            <span className="font-semibold text-gray-700">
                              €{(order.economics?.net_payout ?? order.economics?.total_payout)?.toFixed(2)}
                            </span>
                            {order.economics?.shipping_cost > 0 && (
                              <span className="text-xs text-blue-500 ml-1">(incl. sped. €{order.economics?.shipping_cost?.toFixed(2)})</span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-text-muted mt-1">
                          Consegnato: {order.delivered_at ? new Date(order.delivered_at).toLocaleDateString('it-IT') : '-'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedVendor(null)}>
              Chiudi
            </Button>
            {selectedVendor?.payout_status === 'DA_PAGARE' && (
              <Button 
                onClick={() => setProcessDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700 gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Processa Payout
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Payout Confirmation Dialog */}
      <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              Conferma Payout
            </DialogTitle>
            <DialogDescription>
              Stai per registrare il pagamento per <strong>{selectedVendor?.producer_name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Netto Prodotti</span>
                  <span className="font-semibold">
                    €{vendorOrders?.totals?.net_total?.toFixed(2) || '0.00'}
                  </span>
                </div>
                {(vendorOrders?.totals?.shipping_total > 0) && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Rimborso Spedizione</span>
                    <span className="font-semibold text-blue-600">
                      +€{vendorOrders?.totals?.shipping_total?.toFixed(2)}
                    </span>
                  </div>
                )}
                {(vendorOrders?.totals?.refund_total > 0) && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-orange-600">Rimborsi Resi</span>
                    <span className="font-semibold text-orange-600">
                      -€{vendorOrders?.totals?.refund_total?.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 flex items-center justify-between">
                  <span className="font-medium">TOTALE PAYOUT</span>
                  <span className="text-2xl font-bold text-green-600">
                    €{vendorOrders?.totals?.total_payout?.toFixed(2) || selectedVendor?.total_payout?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
              <div className="text-xs text-text-muted mt-2">
                {vendorOrders?.totals?.orders_ready || selectedVendor?.orders_ready} ordini inclusi
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label>Data Pagamento</Label>
                <Input 
                  value={payoutForm.payment_date}
                  onChange={(e) => setPayoutForm({ ...payoutForm, payment_date: e.target.value })}
                  placeholder="DD/MM/YYYY"
                />
              </div>
              
              <div>
                <Label>Metodo Pagamento</Label>
                <Select 
                  value={payoutForm.payment_method} 
                  onValueChange={(val) => setPayoutForm({ ...payoutForm, payment_method: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bonifico">Bonifico Bancario</SelectItem>
                    <SelectItem value="stripe_connect">Stripe Connect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {payoutForm.payment_method === 'stripe_connect' && selectedVendor && !selectedVendor.stripe_payouts_enabled && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800 flex items-start gap-2">
                    <ShieldOff className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      <strong>Vendor non abilitato ai payout Stripe.</strong>{' '}
                      {!selectedVendor.stripe_account_id
                        ? 'Il vendor non ha collegato il suo account Stripe.'
                        : !selectedVendor.stripe_onboarding_completed
                          ? "Il vendor non ha completato l'onboarding Stripe."
                          : "L'account Stripe del vendor è in fase di verifica."}{' '}
                      Usa Bonifico Bancario oppure attendi l'abilitazione.
                    </span>
                  </p>
                </div>
              )}

              <div>
                <Label>Riferimento Bonifico *</Label>
                <Input
                  value={payoutForm.payment_reference}
                  onChange={(e) => setPayoutForm({ ...payoutForm, payment_reference: e.target.value })}
                  placeholder="Es: SEPA-2026012212345"
                />
              </div>
              
              <div>
                <Label>Note (opzionale)</Label>
                <Textarea 
                  value={payoutForm.notes}
                  onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                  placeholder="Note interne..."
                  rows={2}
                />
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <strong>Attenzione:</strong> Questa azione è irreversibile. Gli ordini saranno marcati come pagati.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleProcessPayout}
              disabled={
                processingPayout ||
                !payoutForm.payment_reference.trim() ||
                (payoutForm.payment_method === 'stripe_connect' && selectedVendor && !selectedVendor.stripe_payouts_enabled)
              }
              className="bg-green-600 hover:bg-green-700 gap-2"
            >
              {processingPayout ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Conferma Payout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPaymentsDashboard;
